import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Trash2, Check, Bell, BellOff, Crown, ArrowUpRight, Info, AlertCircle, Calendar, ChevronDown, ChevronUp, Plus, X, Tag, CheckCircle } from 'lucide-react';
import MobileFreeTrialModal from '../MobileFreeTrialModal';
import AffiliateLink from '../../AffiliateLink';
import { useTranslation } from "../../../../contexts/LanguageContext";
import { useAuth } from "../../../../App"; // Import the auth hook

const MobileMerchantPlansTab = ({ 
  merchant, 
  isAdding, 
  handleAddToList, 
  handleAddToFreeTrial,
  handleTogglePromoNotifications,
  activeSubscription, // Now receives the full subscription object
  onDeleteSubscription,
  userFreeTrialsCount = 0,
  promoNotificationsEnabled = false,
  isPremium = false,
  trackingLimit = { canTrack: true, maxTracked: 5, remaining: 5, currentCount: 0 },
  trackedMerchantsCount = 0,
  onUpgrade = () => {},
  onSubscriptionUpdated = () => {}
}) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Use auth hook
  
  const [isFreeTrialAdding, setIsFreeTrialAdding] = useState(false);
  const [isTogglingPromo, setIsTogglingPromo] = useState(false);
  const [isFreeTrialModalOpen, setIsFreeTrialModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [freeTrials, setFreeTrials] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activePromos, setActivePromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [localFreeTrialsCount, setLocalFreeTrialsCount] = useState(userFreeTrialsCount);
  const [showCustomPlanForm, setShowCustomPlanForm] = useState(false);
  const [customPlanData, setCustomPlanData] = useState({
    name: '',
    price: '',
    interval: 'monthly'
  });
  const [creatingCustomPlan, setCreatingCustomPlan] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFreeTrialReplacementModal, setShowFreeTrialReplacementModal] = useState(false);
  const [planToAdd, setPlanToAdd] = useState(null);
  const [isCancellingFreeTrial, setIsCancellingFreeTrial] = useState(false);

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate("/demo");
    }
  }, [user, navigate]);

  // Update local states when props change
  useEffect(() => {
    setLocalFreeTrialsCount(userFreeTrialsCount);
  }, [userFreeTrialsCount]);

  // Check if user has a custom plan
  const hasCustomPlan = activeSubscription?.is_custom;

  // Check if user has any active plan (custom or non-custom)
  const hasActivePlan = activeSubscription !== null;

  // Update current time every second for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch active promos when merchant changes
  useEffect(() => {
    const fetchActivePromos = async () => {
      if (!merchant?.id || !user) return;
      
      setLoadingPromos(true);
      try {
        // Use httpClient which automatically includes the auth token
        const response = await httpClient.request(
          `/api/merchant-promos?merchant_id=${merchant.id}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setActivePromos(data);
        } else {
          console.error('Failed to fetch promos');
          if (response.status === 401) {
            const revalidated = await validateToken();
            if (!revalidated) {
              navigate("/demo");
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching promos:', error);
        setActivePromos([]);
      } finally {
        setLoadingPromos(false);
      }
    };

    fetchActivePromos();
  }, [merchant?.id, user, httpClient, validateToken, navigate]);

  // Fetch free trials
  useEffect(() => {
    const fetchFreeTrials = async () => {
      try {
        if (!user) return;

        // Use httpClient which automatically includes the auth token
        const response = await httpClient.request('/api/free-trial-list');
        
        if (response.ok) {
          const data = await response.json();
          setFreeTrials(data);
          setLocalFreeTrialsCount(data.length);
        } else {
          if (response.status === 401) {
            const revalidated = await validateToken();
            if (!revalidated) {
              navigate("/demo");
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching free trials:', error);
      }
    };

    fetchFreeTrials();
  }, [user, httpClient, validateToken, navigate]);

  // Helper function to extract values from MongoDB format
  const extractValue = (value) => {
    if (value && typeof value === 'object') {
      if ('$numberInt' in value) return parseInt(value.$numberInt, 10);
      if ('$numberDouble' in value) return parseFloat(value.$numberDouble);
      if ('$numberLong' in value) return parseInt(value.$numberLong, 10);
      if ('$date' in value) return new Date(parseInt(value.$date.$numberLong, 10));
      if ('$oid' in value) return value.$oid;
    }
    return value;
  };

  // Process offers to extract values from MongoDB format
  const processOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    
    return offers.map(offer => ({
      ...offer,
      plan_id: offer.plan_id ? extractValue(offer.plan_id) : null,
      promo_price: offer.promo_price ? extractValue(offer.promo_price) : null,
      discount_percent: offer.discount_percent ? extractValue(offer.discount_percent) : null
    }));
  };

  // Helper function to normalize plan IDs for comparison
  const normalizePlanId = (planId) => {
    if (planId == null) return null;
    return String(planId); // Convert to string for consistent comparison
  };

  // Separate general and specific promos
  const generalPromos = activePromos.filter(promo => {
    const processedOffers = processOffers(promo.offers);
    return processedOffers.some(offer => offer.plan_id === null || offer.plan_id === undefined);
  });

  const getPlanSpecificPromos = (planId) => {
    const normalizedPlanId = normalizePlanId(planId);
    return activePromos.filter(promo => {
      const processedOffers = processOffers(promo.offers);
      return processedOffers.some(offer => {
        const offerPlanId = normalizePlanId(offer.plan_id);
        return offerPlanId !== null && offerPlanId === normalizedPlanId;
      });
    });
  };

  // Check if a plan is currently active - FIXED: Handle plan_id = 0 correctly
  const isPlanActive = (planId) => {
    if (!activeSubscription) return false;
    
    const activePlanId = normalizePlanId(activeSubscription.plan_id);
    const checkPlanId = normalizePlanId(planId);
    
    // Handle case where both are null/undefined
    if (activePlanId === null || checkPlanId === null) return false;
    
    return activePlanId === checkPlanId;
  };

  // Check if a plan is in free trial
  const isPlanInFreeTrial = (planId) => {
    const normalizedPlanId = normalizePlanId(planId);
    return freeTrials.some(trial => 
      trial.merchant_id === merchant.id && 
      normalizePlanId(trial.plan_id) === normalizedPlanId && 
      trial.status === 'active'
    );
  };

  // Check if user has any active free trial for this merchant
  const hasActiveFreeTrialForMerchant = freeTrials.some(trial => 
    trial.merchant_id === merchant.id && 
    trial.status === 'active'
  );

  // Get active free trial for this merchant
  const getActiveFreeTrialForMerchant = () => {
    return freeTrials.find(trial => 
      trial.merchant_id === merchant.id && 
      trial.status === 'active'
    );
  };

  // Cancel free trial function
  const cancelFreeTrialForMerchant = async () => {
    setIsCancellingFreeTrial(true);
    try {
      if (!user) throw new Error("Authentication required");

      const activeFreeTrial = getActiveFreeTrialForMerchant();
      if (!activeFreeTrial) throw new Error("No active free trial found");

      const planId = normalizePlanId(activeFreeTrial.plan_id);

      // Use httpClient which automatically includes the auth token
      const response = await httpClient.request('/api/cancel-free-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: merchant.id,
          plan_id: planId
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel free trial');
      }

      // Refresh free trials
      const refreshResponse = await httpClient.request('/api/free-trial-list');
      
      if (refreshResponse.ok) {
        const newData = await refreshResponse.json();
        setFreeTrials(newData);
        setLocalFreeTrialsCount(newData.length);
      }
      
      return true;
    } catch (error) {
      console.error('Cancel free trial failed:', error);
      setErrorMessage(error.message || t('merchant.messages.cancelTrialError'));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      throw error;
    } finally {
      setIsCancellingFreeTrial(false);
    }
  };

  // Helper function to calculate countdown timer
  const getCountdownTimer = (endDate) => {
    const date = extractValue(endDate);
    const end = new Date(date);
    const diffTime = end - currentTime;
    
    if (diffTime <= 0) {
      return { text: t('merchant.plansTab.promoExpired'), urgent: false };
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    // If more than 7 days, show the date
    if (diffDays > 7) {
      return { 
        text: `${t('merchant.plansTab.validUntil')}: ${formatDate(end)}`, 
        urgent: false 
      };
    }

    // Show countdown timer for less than 7 days
    let timerText = '';
    if (language === 'it') {
      if (diffDays > 0) {
        timerText = `${diffDays}g ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    } else {
      if (diffDays > 0) {
        timerText = `${diffDays}d ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    }

    return { 
      text: timerText, 
      urgent: diffDays <= 3 
    };
  };

  // Format promo text based on offer type
  const formatPromoText = (offer, originalPrice = null) => {
    if (offer.promo_price !== null && offer.promo_price !== undefined) {
      return originalPrice 
        ? `${t('merchant.plansTab.specialPrice')}: €${offer.promo_price} (${t('merchant.plansTab.was')} €${originalPrice})`
        : `${t('merchant.plansTab.specialPrice')}: €${offer.promo_price}`;
    } else if (offer.discount_percent !== null && offer.discount_percent !== undefined) {
      return originalPrice 
        ? `${offer.discount_percent}% ${t('merchant.plansTab.off')} (€${(originalPrice - (originalPrice * offer.discount_percent / 100)).toFixed(2)} ${t('merchant.plansTab.was')} €${originalPrice})`
        : `${offer.discount_percent}% ${t('merchant.plansTab.off')}`;
    }
    return t('merchant.plansTab.specialOffer');
  };

  const formatBillingInterval = (interval) => {
    switch (interval) {
      case 'month':
      case 'monthly':
        return 'mo';
      case 'year':
      case 'yearly':
        return 'yr';
      default:
        return interval;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/D";
    
    const locale = language === 'it' ? 'it-IT' : 'en-US';
    
    return date.toLocaleDateString(locale, { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Toggle expanded features for a specific plan
  const toggleFeaturesExpanded = (planId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleFreeTrialClick = (plan) => {
    setSelectedPlan(plan);
    setIsFreeTrialModalOpen(true);
  };

  const handleConfirmFreeTrial = async (endDate, automaticRenewal) => {
    setIsFreeTrialModalOpen(false);
    setIsFreeTrialAdding(true);
    
    try {
      // Ensure plan ID is normalized for backend
      const planWithNormalizedId = {
        ...selectedPlan,
        id: normalizePlanId(selectedPlan.id)
      };
      
      await handleAddToFreeTrial(planWithNormalizedId, endDate, automaticRenewal);
      setSuccessMessage(t('merchant.messages.freeTrialStarted'));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      // Refresh free trials count using httpClient
      const response = await httpClient.request('/api/free-trial-list');
      
      if (response.ok) {
        const data = await response.json();
        setFreeTrials(data);
        setLocalFreeTrialsCount(data.length);
      }
    } catch (error) {
      console.error('Free trial error details:', error);
      setErrorMessage(error.message || t('merchant.messages.freeTrialError'));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsFreeTrialAdding(false);
    }
  };

  const handleCancelFreeTrial = () => {
    setIsFreeTrialModalOpen(false);
    setSelectedPlan(null);
  };

  const handleCancelFreeTrialPlan = async (plan) => {
    try {
      if (!user) throw new Error("Authentication required");

      const planId = normalizePlanId(plan.id);

      // Use httpClient which automatically includes the auth token
      const response = await httpClient.request('/api/cancel-free-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: merchant.id,
          plan_id: planId
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel free trial');
      }

      // Refresh free trials
      const refreshResponse = await httpClient.request('/api/free-trial-list');
      
      if (refreshResponse.ok) {
        const newData = await refreshResponse.json();
        setFreeTrials(newData);
        setLocalFreeTrialsCount(newData.length);
      }
    } catch (error) {
      console.error('Cancel free trial failed:', error);
      setErrorMessage(error.message || t('merchant.messages.cancelTrialError'));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  // Handle adding a new plan with free trial check
  const handleAddPlanWithCheck = async (plan) => {
    // Check if user has an active free trial
    if (hasActiveFreeTrialForMerchant) {
      setPlanToAdd(plan);
      setShowFreeTrialReplacementModal(true);
      return;
    }
    
    // If no free trial, proceed normally
    handleAddToList(plan);
  };

  // Handle confirming free trial replacement
  const handleConfirmFreeTrialReplacement = async () => {
    try {
      // First cancel the free trial
      await cancelFreeTrialForMerchant();
      
      // Then add the new plan
      await handleAddToList(planToAdd);
      
      setShowFreeTrialReplacementModal(false);
      setPlanToAdd(null);
      
      // Call the subscription updated callback to refresh the list
      if (onSubscriptionUpdated) {
        onSubscriptionUpdated();
      }
    } catch (error) {
      console.error('Error replacing free trial:', error);
      setErrorMessage(error.message || 'Failed to replace free trial');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handlePromoClick = async () => {
    // Check tracking limit for free users
    if (!isPremium && !trackingLimit.canTrack && !promoNotificationsEnabled) {
      setErrorMessage(trackingLimit.error || 'You have reached the limit for tracking merchants');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setIsTogglingPromo(true);
    try {
      await handleTogglePromoNotifications();
    } finally {
      setIsTogglingPromo(false);
    }
  };

  const handleTrackPromoClick = async (promoId, planId, promoType) => {
    try {
      // Use httpClient which automatically includes the auth token
      await httpClient.request('/api/track-promo-click', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          promo_id: promoId,
          merchant_id: merchant.id,
          plan_id: planId ? normalizePlanId(planId) : null,
          promo_type: promoType
        }),
      });
    } catch (error) {
      console.error('Error tracking promo click:', error);
    }
  };

  const handleCreateCustomPlan = async () => {
    // Check if user already has a custom plan for this merchant
    if (hasCustomPlan) {
      setErrorMessage(t('merchant.plansTab.customPlanAlreadyExists') || 'You already have a custom plan for this merchant');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (!customPlanData.name.trim()) {
      setErrorMessage(t('merchant.plansTab.planNameRequired') || 'Plan name is required');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    const price = parseFloat(customPlanData.price);
    if (isNaN(price) || price <= 0) {
      setErrorMessage(t('merchant.plansTab.validPriceRequired') || 'Please enter a valid price');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setIsUpdating(true);
    setCreatingCustomPlan(true);
    
    try {
      if (!user) throw new Error("Authentication required");

      // Create custom plan
      const customPlan = {
        id: `custom-${Date.now()}`,
        name: customPlanData.name.trim(),
        price: price,
        interval: customPlanData.interval,
        billing_interval: customPlanData.interval,
        currency: "EUR",
        is_custom: true,
      };

      console.log('Creating custom plan:', customPlan);
      
      await handleAddPlanWithCheck(customPlan);
      
      setSuccessMessage(t('merchant.plansTab.customPlanCreated') || 'Custom plan created and added to your subscriptions!');
      setShowSuccess(true);
      
      // Reset form
      setCustomPlanData({
        name: '',
        price: '',
        interval: 'monthly'
      });
      setShowCustomPlanForm(false);
      
      // Call the subscription updated callback to refresh the list
      if (onSubscriptionUpdated) {
        onSubscriptionUpdated();
      }
      
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Custom plan creation error:', error);
      setErrorMessage(error.message || t('merchant.plansTab.customPlanError') || 'Failed to create custom plan');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setCreatingCustomPlan(false);
      setIsUpdating(false);
    }
  };

  const isFreeUser = !isPremium;
  const freeTrialLimit = 5;
  const canAddFreeTrial = isFreeUser ? localFreeTrialsCount < freeTrialLimit : true;
  const freeTrialsPercentage = (localFreeTrialsCount / freeTrialLimit) * 100;
  const remainingTrials = freeTrialLimit - localFreeTrialsCount;

  const getTrialsRemainingText = () => {
    if (remainingTrials === 1) {
      return t('merchant.plansTab.trialsRemainingOne', 'You have one free trial remaining.');
    }
    return t('merchant.plansTab.trialsRemainingOther', 'You have {count} free trials remaining.', { count: remainingTrials });
  };

  const isFreePlan = (plan) => {
    const price = parseFloat(plan.price);
    const isPriceZero = price === 0;
    const nameLower = plan.name.toLowerCase();
    
    return isPriceZero || nameLower.includes('free plan') || nameLower === 'free';
  };

  // Normalize merchant plans to ensure consistent plan ID types
  const normalizedPlans = React.useMemo(() => {
    const plans = merchant.subscriptionPlans || [];
    return plans.map(plan => ({
      ...plan,
      id: normalizePlanId(plan.id) // Normalize to string
    }));
  }, [merchant.subscriptionPlans]);

  // Normalize active subscription plan_id
  const normalizedActiveSubscription = React.useMemo(() => {
    if (!activeSubscription) return null;
    return {
      ...activeSubscription,
      plan_id: normalizePlanId(activeSubscription.plan_id)
    };
  }, [activeSubscription]);

  // Loading overlay function
  const renderLoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] pointer-events-auto p-4">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#253985] mx-auto mb-4"></div>
          <h2 className="text-xl font-bold mb-2 text-gray-800">
            {t('subscriptionList.updatingSubscription', 'Updating your subscription')}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('subscriptionList.pleaseWait', 'Please wait while we process your changes...')}
          </p>
          <div className="mt-4 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div className="animate-progress h-full bg-[#253985] rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Free Trial Replacement Modal
  const renderFreeTrialReplacementModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {t('merchant.plansTab.replaceFreeTrialTitle') || 'Replace Free Trial?'}
            </h3>
            <button
              onClick={() => {
                setShowFreeTrialReplacementModal(false);
                setPlanToAdd(null);
              }}
              className="text-gray-400 hover:text-gray-500 p-1"
              disabled={isCancellingFreeTrial}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                {t('merchant.plansTab.replaceFreeTrialMessage') || 'You currently have an active free trial. Adding a new plan will cancel your free trial. Do you want to continue?'}
              </p>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-800">
                    {planToAdd?.name || t('merchant.plansTab.newPlan')}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {planToAdd?.price 
                      ? `€${parseFloat(planToAdd.price || 0).toFixed(2)}/${planToAdd.interval === 'monthly' ? t('common.perMonthShort', 'mo') : t('common.perYearShort', 'yr')}`
                      : t('subscriptionList.priceNotSet', 'Price not set')
                    }
                  </p>
                </div>
                <span className="text-sm text-blue-600 font-medium">
                  {t('merchant.plansTab.willReplace') || 'Will replace free trial'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6 pt-5 border-t border-gray-200">
            <button
              onClick={() => {
                setShowFreeTrialReplacementModal(false);
                setPlanToAdd(null);
              }}
              disabled={isCancellingFreeTrial}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleConfirmFreeTrialReplacement}
              disabled={isCancellingFreeTrial}
              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg flex items-center shadow-md hover:shadow-lg transition-all hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {isCancellingFreeTrial ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('merchant.plansTab.replacing') || 'Replacing...'}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('merchant.plansTab.replaceAndAdd') || 'Replace Free Trial & Add Plan'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Custom Plan Form
  const renderCustomPlanForm = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900">
              {t('merchant.plansTab.createCustomPlan')}
            </h3>
            <button
              onClick={() => setShowCustomPlanForm(false)}
              className="text-gray-400 hover:text-gray-500 p-1"
              disabled={creatingCustomPlan}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('merchant.plansTab.planName')}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
  type="text"
  value={customPlanData.name}
  onChange={(e) => setCustomPlanData(prev => ({
    ...prev,
    name: e.target.value
  }))}
  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985] text-base"
  placeholder={t('merchant.plansTab.enterPlanName')}
  disabled={creatingCustomPlan}
/>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('merchant.plansTab.price')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    €
                  </span>
                  <input
  type="number"
  value={customPlanData.price}
  onChange={(e) => setCustomPlanData(prev => ({
    ...prev,
    price: e.target.value
  }))}
  className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985] text-base"
  placeholder="0.00"
  step="0.01"
  min="0"
  disabled={creatingCustomPlan}
/>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('merchant.plansTab.billingInterval')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['monthly', 'yearly'].map((interval) => (
                    <button
                      key={interval}
                      type="button"
                      onClick={() => setCustomPlanData(prev => ({
                        ...prev,
                        interval
                      }))}
                      disabled={creatingCustomPlan}
                      className={`py-3 text-center rounded-lg transition-all text-sm ${
                        customPlanData.interval === interval
                          ? 'bg-[#253985] text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {interval === 'monthly' 
                        ? t('common.monthly', 'Monthly')
                        : t('common.annual', 'Annual')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Plan Preview */}
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {customPlanData.name || t('merchant.plansTab.yourCustomPlan')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {customPlanData.price 
                      ? `€${parseFloat(customPlanData.price || 0).toFixed(2)}/${customPlanData.interval === 'monthly' ? t('common.perMonthShort', 'mo') : t('common.perYearShort', 'yr')}`
                      : t('subscriptionList.priceNotSet', 'Price not set')
                    }
                  </p>
                </div>
                <span className="flex items-center text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                  <Tag className="w-3 h-3 mr-1" />
                  {t('merchant.plansTab.custom')}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6 pt-5 border-t border-gray-200">
            <button
              onClick={() => setShowCustomPlanForm(false)}
              disabled={creatingCustomPlan}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleCreateCustomPlan}
              disabled={creatingCustomPlan || !customPlanData.name.trim() || !customPlanData.price}
              className={`px-4 py-2.5 bg-[#253985] text-white rounded-lg flex items-center shadow-md hover:shadow-lg transition-all text-sm ${
                (creatingCustomPlan || !customPlanData.name.trim() || !customPlanData.price) 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-[#1a2a6d]'
              }`}
            >
              {creatingCustomPlan ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('merchant.plansTab.creating')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t('merchant.plansTab.createPlan', 'Create Custom Plan')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Custom Plan Card Component - only show if no custom plan exists
  const CustomPlanCard = () => {
    if (hasCustomPlan) {
      return null; // Don't show custom plan card if custom plan already exists
    }

    return (
      <div
        onClick={() => setShowCustomPlanForm(true)}
        className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-5 transition-all duration-300 hover:border-blue-400 hover:shadow-md cursor-pointer bg-gray-50 flex flex-col items-center justify-center text-center min-h-[300px]"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full mb-4 group-hover:from-blue-200 group-hover:to-blue-100 transition-colors">
          <Plus className="w-7 h-7 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {t('merchant.plansTab.createCustomPlan')}
        </h3>
        <p className="text-gray-600 text-sm mb-4 max-w-xs">
          {t('merchant.plansTab.customPlanDescription')}
        </p>
        <div className="flex items-center text-blue-600 font-medium text-sm">
          <span>{t('merchant.plansTab.createYourOwn')}</span>
          <ArrowUpRight className="h-4 w-4 ml-1" />
        </div>
      </div>
    );
  };

  // Show active custom plan in the plans list
  const activeCustomPlan = hasCustomPlan ? {
    id: normalizePlanId(normalizedActiveSubscription?.plan_id),
    name: normalizedActiveSubscription?.plan_name,
    price: normalizedActiveSubscription?.price,
    interval: normalizedActiveSubscription?.billing_interval,
    billing_interval: normalizedActiveSubscription?.billing_interval,
    currency: normalizedActiveSubscription?.currency || "EUR",
    is_custom: true
  } : null;

  // Sort plans by price (lowest to highest) and put custom plan first if it exists
  const sortedPlans = React.useMemo(() => {
    const plans = [...normalizedPlans];
    
    // Sort by price (lowest to highest)
    plans.sort((a, b) => {
      const priceA = parseFloat(a.price || 0);
      const priceB = parseFloat(b.price || 0);
      return priceA - priceB;
    });
    
    // If there's an active custom plan, put it at the beginning
    if (activeCustomPlan) {
      return [activeCustomPlan, ...plans];
    }
    
    return plans;
  }, [normalizedPlans, activeCustomPlan]);

  // Debug log to check plan comparisons
  useEffect(() => {
    console.log('Mobile Debug: Active subscription:', normalizedActiveSubscription);
    console.log('Mobile Debug: Merchant plans:', normalizedPlans);
    normalizedPlans.forEach(plan => {
      console.log(`Mobile Debug: Plan ${plan.id} (type: ${typeof plan.id}) is active: ${isPlanActive(plan.id)}`);
    });
  }, [normalizedActiveSubscription, normalizedPlans]);

  return (
    <>
      {/* Show loading overlay when updating */}
      {isUpdating && renderLoadingOverlay()}
      
      <div className="space-y-4">
        {/* Success and Error Messages */}
        {showSuccess && (
          <div className="fixed top-4 left-4 right-4 z-50 animate-fade-in">
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5" />
                <span className="text-sm">{successMessage}</span>
              </div>
            </div>
          </div>
        )}

        {showError && (
          <div className="fixed top-4 left-4 right-4 z-50 animate-fade-in">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm">{errorMessage}</span>
              </div>
            </div>
          </div>
        )}

        {/* Free Trial Modal */}
        <MobileFreeTrialModal
          isOpen={isFreeTrialModalOpen}
          onCancel={handleCancelFreeTrial}
          onConfirm={handleConfirmFreeTrial}
          planName={selectedPlan?.name}
          merchantName={merchant?.name}
        />

        {/* Free Trial Replacement Modal */}
        {showFreeTrialReplacementModal && renderFreeTrialReplacementModal()}

        {/* Custom Plan Form Modal */}
        {showCustomPlanForm && renderCustomPlanForm()}

        {/* Tracking Limit Warning */}
        {!isPremium && !trackingLimit.canTrack && !promoNotificationsEnabled && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-800 text-sm">
                  Merchant Tracking Limit Reached
                </h3>
                <p className="text-xs text-red-700 mt-1">
                  You're tracking {trackingLimit.currentCount} of {trackingLimit.maxTracked} merchants. 
                  Upgrade to premium to track unlimited merchants for promo notifications.
                </p>
                <button
                  onClick={onUpgrade}
                  className="mt-2 flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-2 rounded-lg text-xs font-medium hover:opacity-90 transition w-full justify-center"
                >
                  <Crown className="h-3 w-3" />
                  Upgrade to Premium
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Free Plan Information */}
        {isFreeUser && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800 mb-2 text-sm">{t('merchant.plansTab.freePlanLimit')}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs text-blue-600 mb-1">
                      <span>{t('merchant.plansTab.freeTrialsUsed')}:</span>
                      <span>{localFreeTrialsCount}/{freeTrialLimit}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                        style={{ width: `${freeTrialsPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-blue-600 mb-1">
                      <span>{t('merchant.plansTab.trackedMerchants', 'Tracked Merchants')}:</span>
                      <span>{trackingLimit.currentCount}/{trackingLimit.maxTracked}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                        style={{ width: `${(trackingLimit.currentCount / trackingLimit.maxTracked) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                {remainingTrials === 0 && (
                  <div className="mt-3">
                    <button
                      onClick={onUpgrade}
                      className="flex items-center gap-2 bg-[#253985] text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#1a2a6d] transition-colors w-full justify-center"
                    >
                      <Crown className="h-3 w-3" />
                      {t('merchant.plansTab.upgradeForUnlimitedTrials')}
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* General Promos Section */}
        {!loadingPromos && generalPromos.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-50 p-4 rounded-lg border border-blue-200">
            <h2 className="text-lg font-bold text-blue-800 mb-3">
              {t('merchant.plansTab.specialOffersAvailable')}
            </h2>
            <div className="space-y-3">
              {generalPromos.map((promo, index) => {
                const countdown = getCountdownTimer(promo.end_date);
                const processedOffers = processOffers(promo.offers);
                const generalOffers = processedOffers.filter(offer => offer.plan_id === null || offer.plan_id === undefined);
                
                return (
                  <div key={index} className={`p-3 rounded-lg ${countdown.urgent ? 'bg-red-50 border border-red-200' : 'bg-white border border-blue-100'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <h3 className="font-semibold text-blue-800 text-sm">{promo.promo_name}</h3>
                      </div>
                      <div className={`text-xs font-medium ${countdown.urgent ? 'text-red-600' : 'text-blue-600'}`}>
                        <div className="flex items-center gap-1">
                          {countdown.urgent && (
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                          )}
                          <span>{countdown.text}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2">
                      <div className="flex flex-col">
                        {generalOffers.map((offer, offerIndex) => (
                          <span key={offerIndex} className="text-amber-600 font-bold text-sm mb-1">
                            {formatPromoText(offer)}
                          </span>
                        ))}
                        <span className="text-xs text-gray-600">{t('merchant.plansTab.visitWebsiteForOffers')}</span>
                      </div>
                      
                      {promo.affiliate_link && (
                        <div className="mt-2">
                          <a
                            href={promo.affiliate_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#253985] hover:bg-[#1a2a6d] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors block text-center"
                            onClick={() => handleTrackPromoClick(promo._id, null, 'general')}
                          >
                            {t('merchant.plansTab.viewOffers')}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Merchant Actions */}
        <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePromoClick}
              disabled={isTogglingPromo || (!isPremium && !trackingLimit.canTrack && !promoNotificationsEnabled)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                promoNotificationsEnabled 
                  ? 'bg-[#253985] text-white border border-[#1a2a6d]' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-50'
              } ${
                (!isPremium && !trackingLimit.canTrack && !promoNotificationsEnabled) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isTogglingPromo ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : promoNotificationsEnabled ? (
                <Bell className="h-4 w-4" />
              ) : (
                <BellOff className="h-4 w-4" />
              )}
              {promoNotificationsEnabled ? t('merchant.plansTab.promoAlertsOn') : t('merchant.plansTab.promoAlertsOff')}
            </button>
            
            {/* Tracking Status */}
            <div className="text-xs text-gray-600">
              {isPremium ? (
                <span className="flex items-center gap-1 text-green-600">
                  <Crown className="h-3 w-3" />
                  Premium
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  Tracked: {trackingLimit.currentCount}/{trackingLimit.maxTracked}
                  {!trackingLimit.canTrack && (
                    <AlertCircle className="h-3 w-3 text-amber-600" />
                  )}
                </span>
              )}
            </div>
          </div>

          {isFreeUser && remainingTrials > 0 && (
            <div className="text-center text-xs text-gray-600">
              <span className="font-medium">{remainingTrials}</span> {t('merchant.plansTab.freeTrialsLeft')}
            </div>
          )}
        </div>

        <h2 className="text-lg font-semibold text-gray-800">{t('merchant.common.availablePlans')}</h2>
        
        {/* Plans List */}
        <div className="space-y-4">
          {sortedPlans.map((plan, index) => {
            const isCustomPlan = plan.is_custom;
            const isActive = isPlanActive(plan.id);
            const isFreeTrialActive = isPlanInFreeTrial(plan.id);
            const planSpecificPromos = getPlanSpecificPromos(plan.id);
            const isFreePlanActive = isFreePlan(plan);
            const features = plan.features || [];
            const isExpanded = expandedFeatures[plan.id];
            const maxFeaturesToShow = 3;
            const featuresToDisplay = isExpanded ? features : features.slice(0, maxFeaturesToShow);
            const hasMoreFeatures = features.length > maxFeaturesToShow;
            
            return (
              <div
                key={index}
                className={`bg-white p-4 rounded-xl border-2 transition-all duration-300 relative ${
                  isActive 
                    ? "border-blue-500 bg-gradient-to-b from-blue-50/70 to-white shadow-lg" 
                    : isFreeTrialActive
                    ? "border-amber-500 bg-gradient-to-b from-amber-50/70 to-white shadow-lg"
                    : isFreePlanActive
                    ? "border-purple-500 bg-gradient-to-b from-purple-50 to-white shadow-lg"
                    : isCustomPlan
                    ? "border-blue-500 bg-gradient-to-b from-blue-50/50 to-white shadow-lg"
                    : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                }`}
              >
                {/* Plan Badges */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {isCustomPlan && (
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center shadow-sm">
                      <Tag className="h-3 w-3 mr-1" />
                      {t('merchant.plansTab.custom') || 'Custom'}
                    </div>
                  )}
                  
                  {isActive && (
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center shadow-sm">
                      <Check className="h-3 w-3 mr-1" />
                      {t('merchant.common.active')}
                    </div>
                  )}
                  
                  {isFreeTrialActive && (
                    <div className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium flex items-center shadow-sm">
                      <Calendar className="h-3 w-3 mr-1" />
                      {t('merchant.common.freeTrial')}
                    </div>
                  )}

                  {isFreePlanActive && !isActive && !isFreeTrialActive && !isCustomPlan && (
                    <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium flex items-center shadow-sm">
                      <Check className="h-3 w-3 mr-1" />
                      {t('merchant.common.freePlan') || 'Free Plan'}
                    </div>
                  )}
                </div>

                {/* Plan Name and Price */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-gray-900">
                      {isFreePlanActive ? t('merchant.common.free') || 'Free' : `€${plan.price}`}
                    </span>
                    {!isFreePlanActive && plan.price > 0 && (
                      <span className="ml-2 text-sm text-gray-500 font-medium">
                        /{formatBillingInterval(plan.billing_interval || plan.interval)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Plan-Specific Promos - Don't show for custom plans */}
                {!isCustomPlan && planSpecificPromos.length > 0 && (
                  <div className="mb-4">
                    {planSpecificPromos.map((promo, promoIndex) => {
                      const countdown = getCountdownTimer(promo.end_date);
                      const processedOffers = processOffers(promo.offers);
                      const specificOffers = processedOffers.filter(offer => {
                        const offerPlanId = normalizePlanId(offer.plan_id);
                        return offerPlanId !== null && offerPlanId === normalizePlanId(plan.id);
                      });
                      
                      return (
                        <div 
                          key={promoIndex} 
                          className={`border rounded-lg p-2 mb-2 ${
                            countdown.urgent 
                              ? "bg-gradient-to-r from-red-50 to-red-100 border-red-200" 
                              : "bg-gradient-to-r from-amber-50 to-yellow-100 border-amber-200"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center">
                              <span className={`text-xs font-bold px-2 py-1 rounded mr-2 ${
                                countdown.urgent
                                  ? "bg-red-500 text-white"
                                  : "bg-amber-500 text-white"
                              }`}>
                                {t('merchant.plansTab.promo')}
                              </span>
                              <span className={`text-xs font-medium ${
                                countdown.urgent ? "text-red-800" : "text-amber-800"
                              }`}>
                                {promo.promo_name}
                              </span>
                            </div>
                            <div className={`text-xs ${countdown.urgent ? "font-bold text-red-700" : "text-amber-700"}`}>
                              <div className="flex items-center gap-1">
                                {countdown.urgent && (
                                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                                )}
                                <span>{countdown.text}</span>
                              </div>
                            </div>
                          </div>
                          
                          {specificOffers.map((offer, offerIndex) => (
                            <div key={offerIndex} className="text-xs font-semibold mb-1">
                              <span className={countdown.urgent ? "text-red-700" : "text-amber-700"}>
                                {formatPromoText(offer, plan.price)}
                              </span>
                            </div>
                          ))}
                          
                          {promo.affiliate_link && (
                            <div className="mt-2">
                              <a
                                href={promo.affiliate_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-block w-full text-center py-1.5 rounded-md text-xs font-medium ${
                                  countdown.urgent 
                                    ? "bg-red-600 hover:bg-red-700 text-white" 
                                    : "bg-amber-600 hover:bg-amber-700 text-white"
                                } transition-colors`}
                                onClick={() => handleTrackPromoClick(
                                  promo._id, 
                                  plan.id, 
                                  specificOffers[0]?.promo_type || 'specific'
                                )}
                              >
                                {t('merchant.plansTab.getOfferNow')}
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Features List */}
                <div className="mb-4">
                  <div className="space-y-2">
                    {featuresToDisplay.map((feature, i) => (
                      <div key={i} className="flex items-start">
                        <svg
                          className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-gray-700 text-xs">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Show More/Less Toggle */}
                  {hasMoreFeatures && (
                    <button
                      onClick={() => toggleFeaturesExpanded(plan.id)}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          {t('merchant.plansTab.showLess')}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          {t('merchant.plansTab.showMore')}
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {isActive ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSubscription();
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium text-sm"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('merchant.common.removeSubscription')}
                    </button>
                  ) : isFreeTrialActive ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelFreeTrialPlan(plan);
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium text-sm"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('merchant.plansTab.cancelFreeTrial')}
                    </button>
                  ) : isFreePlanActive ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("Cancel free plan:", plan);
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-gray-500 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium text-sm"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('merchant.common.cancel') || 'Cancel'}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("Adding/replacing plan:", plan);
                          handleAddPlanWithCheck(plan);
                        }}
                        disabled={isAdding}
                        className="w-full py-2.5 bg-[#253985] hover:bg-[#1a2a6d] text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium text-sm"
                      >
                        {isAdding ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {activeSubscription 
                              ? (t('merchant.plansTab.replacing') || 'Replacing...') 
                              : (t('merchant.plansTab.adding') || 'Adding...')
                            }
                          </>
                        ) : (
                          activeSubscription 
                            ? (t('merchant.plansTab.replaceSubscription') || 'Replace Subscription') 
                            : t('merchant.common.addSubscription')
                        )}
                      </button>

                      {/* Don't show free trial button for custom plans OR if user has active plan OR if user has active free trial */}
                      {!isCustomPlan && !hasActivePlan && !hasActiveFreeTrialForMerchant && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFreeTrialClick(plan);
                          }}
                          disabled={isFreeTrialAdding || !canAddFreeTrial}
                          className={`w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium text-sm ${
                            !canAddFreeTrial ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {isFreeTrialAdding ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {t('merchant.plansTab.adding')}
                            </>
                          ) : (
                            t('merchant.plansTab.addAsFreeTrial')
                          )}
                        </button>
                      )}

                      {/* Affiliate Link - only for non-custom plans */}
                      {!isCustomPlan && (
                        <div className="text-center pt-2">
                          <AffiliateLink
                            linkType="textlink"
                            merchantId={merchant.id}
                            planId={plan.id}
                            fallbackUrl={merchant.website}
                            className="text-[#253985] hover:text-[#1a2a6d] text-xs font-medium"
                          >
                            {t('merchant.plansTab.visitWebsiteToSubscribe')}
                          </AffiliateLink>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Free Trial Limit Warning - only for non-custom plans */}
                {!canAddFreeTrial && isFreeUser && !isActive && !isFreeTrialActive && !isFreePlanActive && !isCustomPlan && !hasActiveFreeTrialForMerchant && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        {t('merchant.plansTab.freePlanLimitReached')}{' '}
                        <button 
                          onClick={onUpgrade}
                          className="font-medium underline hover:no-underline text-[#253985]"
                        >
                          {t('merchant.plansTab.upgradeNow')}
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Custom Plan Card - Only show if no custom plan exists */}
          <CustomPlanCard />
        </div>
      </div>
    </>
  );
};

export default MobileMerchantPlansTab;